import {
    ApolloClient,
    NormalizedCacheObject,
} from '@apollo/client'
import { ApolloCacheKey } from '@deltek/specification-client/core/Configurations/apolloBaseClient'
import { getLocalStorageKeyValueSize } from '@deltek/specification-client/core/Utilities/localstorageUtils'
import { GetProjectElementNodesQuery } from 'api/graphQL/queries/projectElements.queries'
import { CachePersistor } from 'apollo-cache-persist'
import debounce from 'lodash.debounce'

/** Maximum Apollo cache size in MB before evicting workspace tree data */
const MAX_CACHE_SIZE_MB = 4

/** Debounce interval in ms before running cache eviction after a watch callback */
const EVICTION_DEBOUNCE_MS = 1000

/**
 * Watches the Apollo cache for changes to GetProjectElementNodesQuery and
 * selectively evicts workspace tree data when localStorage usage exceeds
 * the size threshold. This prevents the browser's ~5 MB localStorage quota
 * from being exceeded while preserving all other cached data.
 *
 * Returns a cleanup function that cancels any pending debounce and
 * unsubscribes the watcher. Must be called on component unmount to prevent
 * accumulating duplicate watchers.
 */
export const cacheWatcher = (client: ApolloClient<NormalizedCacheObject>, persistor: CachePersistor<unknown>) => {
    /** Checks cache size and evicts only workspace tree data if over threshold */
    const evictWorkspaceData = async () => {
        const size = getLocalStorageKeyValueSize(ApolloCacheKey)
        if (size < MAX_CACHE_SIZE_MB) {
            return
        }

        console.warn(
            `[ApolloCacheWatcher] Cache size (${size}MB) exceeds ${MAX_CACHE_SIZE_MB}MB. ` +
            'Evicting workspace tree data (getProjectElementNodes).'
        )

        // Evict only workspace tree data instead of cache.reset() which nukes everything
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'getProjectElementNodes' })
        client.cache.gc() // reclaim memory from evicted references
        await persistor.persist() // re-persist the now-smaller cache to localStorage
    }

    // Debounce to avoid rapid-fire size checks during quick successive cache writes
    const debouncedEviction = debounce(evictWorkspaceData, EVICTION_DEBOUNCE_MS)

    // Watch only GetProjectElementNodesQuery — the largest query in the app.
    const unsubscribe = client.cache.watch({
        query: GetProjectElementNodesQuery,
        optimistic: true,
        callback: debouncedEviction,
    })

    // Cleanup: cancel pending debounce timer and unsubscribe the watcher
    return () => {
        debouncedEviction.cancel()
        unsubscribe()
    }
}
